import { ApolloClient, ApolloProvider, InMemoryCache, createHttpLink, ApolloLink, fromPromise } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { getAuthToken } from '../axios/auth-token';
import { clearAuthData, addToFailedQueue } from '../axios/auth-event';

const graphqlUri =
  process.env.NEXT_PUBLIC_API_V2_BASE_URL
    ? `${process.env.NEXT_PUBLIC_API_V2_BASE_URL}/graphql`
    : process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
      ? 'https://admin-lb.clickster.io/api/v2/graphql'
      : 'https://s-admin-lb.clickster.io/api/v2/graphql';

const httpLink = createHttpLink({
  uri: graphqlUri,
});

const authLink = setContext((_, { headers }) => {
  const token = getAuthToken();
  return {
    headers: {
      ...headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
});

/**
 * Error handling link for Apollo client
 * Handles 401 unauthorized errors similar to Axios interceptors
 * Queues failed operations for retry after successful login
 */
const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  // Handle network errors with status code (like HTTP 401)
  if (networkError && 'statusCode' in networkError) {
    const statusCode = networkError.statusCode;
    
    // Handle 401 Unauthorized errors
    if (statusCode === 401) {
      // Ensure we don't retry indefinitely
      const context = operation.getContext();
      const isRetry = context._retry === true;
      
      if (!isRetry) {
        // Mark as retried - using original operation, not a copy
        operation.setContext({ ...context, _retry: true });
        
        // Clear auth data from storage
        clearAuthData();
        
        // Queue the failed GraphQL operation for retry
        return fromPromise(
          addToFailedQueue({
            // Create a compatible config object for our queue system
            url: operation.getContext().uri,
            method: 'POST',
            data: {
              query: operation.query.loc?.source.body,
              variables: operation.variables
            },
            headers: operation.getContext().headers,
            _graphQLOperation: operation // Store the reference to original operation
          })
        ).flatMap(() => {
          // This will be called after login when the queue is processed
          return forward(operation);
        });
      }
    }
  }

  // Handle specific GraphQL errors that indicate auth issues
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      // Check for auth errors based on error codes or messages
      if (err.extensions?.code === 'UNAUTHENTICATED' || 
          err.message.includes('not authenticated') ||
          err.message.includes('not authorized')) {
        
        const context = operation.getContext();
        const isRetry = context._retry === true;
        
        if (!isRetry) {
          // Mark as retried - using original operation, not a copy
          operation.setContext({ ...context, _retry: true });
          
          // Clear auth data from storage
          clearAuthData();
          
          // Queue the failed GraphQL operation for retry
          return fromPromise(
            addToFailedQueue({
              url: operation.getContext().uri,
              method: 'POST',
              data: {
                query: operation.query.loc?.source.body,
                variables: operation.variables
              },
              headers: operation.getContext().headers,
              _graphQLOperation: operation // Use the original operation reference
            })
          ).flatMap(() => {
            return forward(operation);
          });
        }
      }
    }
  }
});
const cache = new InMemoryCache();

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([authLink, errorLink, httpLink]),
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'no-cache',
    },
    query: {
      fetchPolicy: 'no-cache',
    },
    mutate: {
      fetchPolicy: 'no-cache',
    },
  },
});

export const GraphqlProvider = ({ children }: { children: React.ReactNode }) => {


  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};
